import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { CenteredLoader, InlineLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import Select from '../../components/form/AppSelect';
import '../../shared/ReactSelectDarkMode.css';
import { useQuery, useMutation } from '@tanstack/react-query';
import PageForm from '../../components/layout/PageForm';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

// Debounce function to limit API calls during typing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const TransportForm = () => {
  const { transportId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = Boolean(transportId);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    licence_plate: '',
    available: true,
    next_technical_check_date: ''
  });

  const [categories, setCategories] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const { isLoading: loading } = useQuery({
    queryKey: ['transport-form', transportId],
    enabled: isEdit,
    queryFn: async () => {
      const response = await api.get(`transport/api/vehicles/${transportId}/`);
      setFormData({
        name: response.name || '',
        category: response.category || '',
        description: response.description || '',
        licence_plate: response.licence_plate || '',
        available: response.available !== undefined ? response.available : true,
        next_technical_check_date: response.next_technical_check_date || ''
      });
      return response;
    },
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      setCategoryOptions(categories.map(category => ({
        value: category.id,
        label: category.name,
        color: category.color
      })));
    }
  }, [categories]);

  const fetchCategories = async (searchTerm = '') => {
    try {
      let url = 'transport/api/categories/';
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await api.get(url);
      setCategories(response || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Debounced search function
  const debouncedCategorySearch = useCallback(
    debounce((inputValue) => {
      fetchCategories(inputValue);
    }, 300),
    []
  );

  const submitMutation = useMutation({
    mutationFn: (vehicleData) => isEdit
      ? api.put(`transport/api/vehicles/${transportId}/`, vehicleData)
      : api.post('transport/api/vehicles/', vehicleData),
    onSuccess: () => navigate('/transport'),
    onError: (err) => setError(isEdit ? t('transport.errors.failedToUpdate') : t('transport.errors.failedToCreate')),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    setError(null);
    const nextDate = formData.next_technical_check_date;
    const vehicleData = {
      ...formData,
      category: formData.category || null,
      next_technical_check_date: (nextDate && nextDate.trim() !== '') ? nextDate : null
    };
    submitMutation.mutate(vehicleData);
  };
  const submitting = submitMutation.isPending;

  const handleCancel = () => {
    navigate('/transport');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const getTechnicalCheckStatus = () => {
    if (!formData.next_technical_check_date) return null;

    const today = new Date();
    const checkDate = new Date(formData.next_technical_check_date);
    const diffTime = checkDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        type: 'danger',
        title: t('transport.technicalCheck.overdue'),
        message: `${Math.abs(diffDays)} ${t('transport.daysOverdue')}`
      };
    } else if (diffDays <= 30) {
      return {
        type: 'warning',
        title: t('transport.technicalCheck.dueSoon'),
        message: `${diffDays} ${t('transport.daysRemaining')}`
      };
    } else {
      return {
        type: 'success',
        title: t('transport.technicalCheck.ok'),
        message: `${diffDays} ${t('transport.daysRemaining')}`
      };
    }
  };

  if (loading) {
    return (
      <PageShell pretitle={t('transport.title')} title={isEdit ? t('transport.editVehicle') : t('transport.addVehicle')}>
        <PageSection>
          <CenteredLoader message={t('transport.loading')} />
        </PageSection>
      </PageShell>
    );
  }

  const technicalStatus = getTechnicalCheckStatus();
  const pageTitle = isEdit ? t('transport.editVehicle') : t('transport.addVehicle');

  return (
    <PageShell
      pretitle={(
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/transport" className="text-reset">
                {t('transport.title')}
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {isEdit ? t('transport.editVehicle') : t('transport.addVehicle')}
            </li>
          </ol>
        </nav>
      )}
      title={pageTitle}
      description={isEdit ? t('transport.editVehicleDescription') : t('transport.addVehicleDescription')}
      actions={(
        <Link
          to="/transport"
          className="btn btn-outline-secondary"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-arrow-left" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M5 12l14 0"></path>
            <path d="M5 12l6 6"></path>
            <path d="M5 12l6 -6"></path>
          </svg>
          {t('common.back')}
        </Link>
      )}
    >
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <PageForm
            title={pageTitle}
            description={t('transport.formDescription', 'Spravuj základní data vozidla, dostupnost a technickou kontrolu v jednom sjednoceném editoru.')}
            onSubmit={handleSubmit}
            footer={
              <>
                <Link to="/transport" className="btn btn-outline-secondary">
                  {t('common.cancel')}
                </Link>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <InlineLoader className="me-2" />
                      {isEdit ? t('common.updating') : t('common.creating')}
                    </>
                  ) : (
                    isEdit ? t('common.update') : t('common.create')
                  )}
                </button>
              </>
            }
          >
            {error && <Alert variant="danger" className="app-inline-alert mb-0">{error}</Alert>}

            <h4 className="form-section-title app-form-section-title">{t('transport.vehicleInformation')}</h4>

            <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label required">{t('transport.fields.name')}</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.name ? 'is-invalid' : ''}`}
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                            placeholder={t('transport.placeholders.name')}
                          />
                          {errors?.name && (
                            <div className="invalid-feedback">{Array.isArray(errors.name) ? errors.name[0] : errors.name}</div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">{t('transport.fields.licencePlate')}</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.licence_plate ? 'is-invalid' : ''}`}
                            value={formData.licence_plate}
                            onChange={(e) => handleInputChange('licence_plate', e.target.value)}
                            placeholder={t('transport.placeholders.licencePlate')}
                          />
                          {errors?.licence_plate && (
                            <div className="invalid-feedback">{Array.isArray(errors.licence_plate) ? errors.licence_plate[0] : errors.licence_plate}</div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">{t('transport.fields.category')}</label>
                          <Select
                            value={categoryOptions.find(option => option.value === formData.category) || null}
                            onChange={(selectedOption) =>
                              handleInputChange('category', selectedOption ? selectedOption.value : '')
                            }
                            options={categoryOptions}
                            isClearable
                            isSearchable
                            onInputChange={debouncedCategorySearch}
                            placeholder={t('transport.placeholders.category')}
                            noOptionsMessage={() => t('common.noOptions')}
                            theme={selectTheme}
                            classNamePrefix="react-select"
                            className={errors?.category ? 'is-invalid' : ''}
                          />
                          {errors?.category && (
                            <div className="invalid-feedback d-block">{Array.isArray(errors.category) ? errors.category[0] : errors.category}</div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">{t('transport.fields.nextTechnicalCheck')}</label>
                          <input
                            type="date"
                            className={`form-control ${errors?.next_technical_check_date ? 'is-invalid' : ''}`}
                            value={formData.next_technical_check_date}
                            onChange={(e) => handleInputChange('next_technical_check_date', e.target.value)}
                          />
                          {errors?.next_technical_check_date && (
                            <div className="invalid-feedback">{Array.isArray(errors.next_technical_check_date) ? errors.next_technical_check_date[0] : errors.next_technical_check_date}</div>
                          )}
                          <small className="form-hint">
                            {t('transport.hints.technicalCheck')}
                          </small>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label required">{t('transport.fields.description')}</label>
                          <textarea
                            className={`form-control ${errors?.description ? 'is-invalid' : ''}`}
                            rows="4"
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            required
                            placeholder={t('transport.placeholders.description')}
                          />
                          {errors?.description && (
                            <div className="invalid-feedback">{Array.isArray(errors.description) ? errors.description[0] : errors.description}</div>
                          )}
                        </div>
                      </div>
            </div>

            <h4 className="form-section-title app-form-section-title">{t('transport.settings')}</h4>

            <div className="mb-3">
              <label className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formData.available}
                  onChange={(e) => handleInputChange('available', e.target.checked)}
                />
                <span className="form-check-label">{t('transport.fields.available')}</span>
              </label>
              <small className="form-hint">
                {formData.available ? t('transport.hints.availableTrue') : t('transport.hints.availableFalse')}
              </small>
            </div>

            {technicalStatus && (
              <Alert variant={technicalStatus.type} className="app-inline-alert mb-0">
                <strong>{t('transport.technicalCheck.preview')}:</strong>{' '}
                {new Date(formData.next_technical_check_date).toLocaleDateString()} - {technicalStatus.title}, {technicalStatus.message}
              </Alert>
            )}
          </PageForm>
        </div>
      </div>
    </PageShell>
  );
};

export default TransportForm;
